import { toast } from "sonner";
import {
  Lightning,
  Microphone,
  FileText,
  ArrowRight,
  CheckCircle,
  X,
} from "phosphor-react";

/**
 * SmartToolsSuccessToast - Rich post-purchase success notification
 * 
 * Shows congratulations + quick-start tips for Smart Tools features:
 * - Voice Notes: Record voice notes on any job
 * - Job Summaries: AI-generated summaries from job details
 * 
 * Navigation tips to help users get started immediately.
 */

const quickStartTips = [
  {
    icon: Microphone,
    title: "Voice Notes",
    tip: "Open any job → tap the microphone to dictate notes",
    path: "/jobs",
  },
  {
    icon: FileText,
    title: "AI Job Summaries",
    tip: "Create a job → click 'Generate Summary' for instant AI descriptions",
    path: "/jobs/new",
  },
];

/**
 * Show the Smart Tools success toast with quick-start tips
 * @param {Function} navigate - React Router navigate function
 */
export function showSmartToolsSuccessToast(navigate) {
  toast.custom(
    (t) => (
      <div
        className="w-full max-w-md bg-white border-2 border-emerald-300 rounded-lg shadow-xl overflow-hidden"
        data-testid="smart-tools-success-toast"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
              <Lightning size={18} className="text-white" weight="fill" />
            </div>
            <div>
              <p className="text-white font-bold text-sm">Smart Tools Activated!</p>
              <p className="text-emerald-100 text-xs">Your AI features are ready</p>
            </div>
          </div>
          <button
            onClick={() => toast.dismiss(t)}
            className="text-white/70 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <X size={18} />
          </button>
        </div>

        {/* Quick Start Tips */}
        <div className="p-4 space-y-3">
          <p className="text-xs text-zinc-500 uppercase font-medium tracking-wide">
            Quick Start Tips
          </p>
          
          {quickStartTips.map((item, idx) => {
            const IconComp = item.icon;
            return (
              <button
                key={idx}
                onClick={() => {
                  toast.dismiss(t);
                  if (navigate && item.path) {
                    navigate(item.path);
                  }
                }}
                className="w-full flex items-start gap-3 p-3 bg-zinc-50 hover:bg-zinc-100 rounded-lg transition-colors text-left group"
                data-testid={`quick-tip-${idx}`}
              >
                <div className="w-9 h-9 bg-[#FF6600]/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#FF6600]/20 transition-colors">
                  <IconComp size={18} className="text-[#FF6600]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-zinc-900 flex items-center gap-1">
                    {item.title}
                    <ArrowRight size={12} className="text-zinc-400 group-hover:text-[#FF6600] transition-colors" />
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">{item.tip}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 bg-zinc-50 border-t border-zinc-100 flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-emerald-600">
            <CheckCircle size={14} weight="fill" />
            <span className="text-xs font-medium">Unlimited AI uses included</span>
          </div>
          <button
            onClick={() => toast.dismiss(t)}
            className="text-xs text-zinc-500 hover:text-zinc-700"
          >
            Got it
          </button>
        </div>
      </div>
    ),
    {
      duration: 12000, // 12 seconds - long enough to read tips
      position: "top-center",
    }
  );
}

export default showSmartToolsSuccessToast;
